/**
 * Interior Execution — unit list (P1).
 *
 * Lists every unit whose latest design is developer-approved and ready for
 * execution. Filters: Project, Config name (dropdowns derived from the loaded
 * cards) + a single advanced text search (buyer / unit / project / phase /
 * designer / config — matched server-side). Clicking a card opens the existing
 * Interior Execution flow at /interior-execution/:leadId.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Search, Package, CalendarClock, AlertTriangle, ChevronRight } from "lucide-react";
import { getExecutionUnits } from "../../services/interiorExecution";

const PAGE_SIZE = 24;

const STATE_STYLE = {
  NOT_CONFIGURED: { label: "Not configured", cls: "bg-slate-100 text-slate-600" },
  CONFIGURED: { label: "Configured", cls: "bg-sky-50 text-sky-700" },
  PLAN_ACTIVE: { label: "In execution", cls: "bg-flamingo-50 text-flamingo-700" },
  COMPLETED: { label: "Completed", cls: "bg-emerald-50 text-emerald-700" },
};
const URGENCY_STYLE = {
  OPEN: "text-emerald-600",
  LOCK_IMMINENT: "text-amber-600",
  LOCK_PASSED: "text-rose-600",
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const ExecutionUnits = ({ basePath = "/interior-execution", embedded = false, controlledFilters = null }) => {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [configFilter, setConfigFilter] = useState("");

  // When controlledFilters is supplied (admin shared filter bar), all filtering is
  // server-side and the internal filter bar is hidden.
  const cfKey = controlledFilters ? JSON.stringify(controlledFilters) : "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (controlledFilters) {
        const cf = controlledFilters;
        if (cf.search) params.search = cf.search;
        if (cf.developerId) params.developerId = cf.developerId;
        if (cf.projectId) params.projectId = cf.projectId;
        if (cf.projectPhaseId) params.projectPhaseId = cf.projectPhaseId;
        if (cf.propertyConfigId) params.propertyConfigId = cf.propertyConfigId;
      } else if (search) {
        params.search = search;
      }
      const res = await getExecutionUnits(params);
      setCards(res.items || []);
      setTotalPages(res.totalPages || 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load execution units");
      setCards([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, cfKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  // Reset to page 1 when the admin filters change.
  useEffect(() => { setPage(1); }, [cfKey]);

  // Debounce the text search.
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setSearch(searchInput.trim()); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Project / config dropdowns derived from the loaded page (client-side filter on top).
  const projectOptions = useMemo(
    () => [...new Set(cards.map((c) => c.projectName).filter(Boolean))].sort(),
    [cards]
  );
  const configOptions = useMemo(
    () => [...new Set(cards.map((c) => c.configName).filter(Boolean))].sort(),
    [cards]
  );
  const visible = useMemo(
    () => controlledFilters
      ? cards // admin filters are applied server-side
      : cards.filter((c) =>
          (!projectFilter || c.projectName === projectFilter) &&
          (!configFilter || c.configName === configFilter)
        ),
    [cards, projectFilter, configFilter, controlledFilters]
  );

  return (
    <div className={embedded ? "" : "p-6 max-w-7xl mx-auto"}>
      {!embedded && (
        <div className="mb-5">
          <h1 className="text-xl font-bold text-slate-900 inline-flex items-center gap-2">
            <Package size={22} className="text-flamingo-600" /> Interior Execution
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Developer-approved units ready for execution.</p>
        </div>
      )}

      {/* filters (hidden when an admin shared filter bar controls this list) */}
      {!controlledFilters && (
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search buyer, unit, project, phase, designer, config…"
              className="w-full rounded-xl border border-slate-300 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40"
            />
          </div>
          <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}
            className="rounded-xl border border-slate-300 py-2.5 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-flamingo-500/40">
            <option value="">All projects</option>
            {projectOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={configFilter} onChange={(e) => setConfigFilter(e.target.value)}
            className="rounded-xl border border-slate-300 py-2.5 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-flamingo-500/40">
            <option value="">All configs</option>
            {configOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-40 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Package size={40} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">No units ready for execution{search ? " match your search" : ""}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((c) => {
            const st = STATE_STYLE[c.state] || STATE_STYLE.NOT_CONFIGURED;
            return (
              <button
                key={c.idLeadId}
                onClick={() => navigate(`${basePath}/${c.idLeadId}`)}
                className="text-left rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-md hover:border-flamingo-200 transition-all group"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{c.unitNo || `Unit ${c.propertyId}`}</p>
                    <p className="text-xs text-slate-500 truncate">{c.projectName} · {c.phaseName}</p>
                  </div>
                  <span className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                </div>

                <div className="mt-3 space-y-1 text-xs text-slate-600">
                  <p><span className="text-slate-400">Buyer:</span> {c.buyerName || "—"}</p>
                  <p><span className="text-slate-400">Config:</span> {c.configName || "—"}</p>
                  <p><span className="text-slate-400">Designer:</span> {c.designerName || "—"}</p>
                </div>

                {c.totalPackages > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                      <span>Packages</span>
                      <span>{c.completedPackages}/{c.totalPackages}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-emerald-400" style={{ width: `${(c.completedPackages / c.totalPackages) * 100}%` }} />
                    </div>
                  </div>
                )}

                <div className="mt-3 flex items-center justify-between">
                  {c.nextDesignLockDate ? (
                    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${URGENCY_STYLE[c.nextLockUrgency] || "text-slate-500"}`}>
                      {c.nextLockUrgency === "LOCK_PASSED" ? <AlertTriangle size={11} /> : <CalendarClock size={11} />}
                      Next lock {fmt(c.nextDesignLockDate)}
                    </span>
                  ) : <span className="text-[11px] text-slate-400">No pending locks</span>}
                  <ChevronRight size={16} className="text-slate-300 group-hover:text-flamingo-500 transition-colors" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm disabled:opacity-40">Previous</button>
          <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
};

export default ExecutionUnits;
